import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	getEntitiesById,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
} from '../../utils/DataAccessUtils';
import { standardInitialStateGenerator } from '../../reducers/standard_reducer_utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supplierScorecardsRestCrudThunks } from '../../thunks/supplier_scorecards_thunks';
import { SUPPLIER_FACILITIES_CRUD_ACTION_CREATORS } from '../../actions/supplier_facilities_actions';
import { capitalizeEachWord } from '../../utils/DataFormatterUtils';
import {
	formatDurationShort,
	roundToOneDecimalPlace,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import {
	supplierPerformanceFiltersToApiParams,
	SupplierPerformanceFilters,
} from '../kpi_analytics_overview_supplier_performance_page/SupplierPerformanceUtils';
import { DEFAULT_COMPLETED_AT_SPAN_MONTHS } from '../kpi_analytics_overview_supplier_performance_page/SupplierPerformanceConstants';
import moment from 'moment';
import { AddressDisplay } from '../address_display/AddressDisplay';
import * as React from 'react';
import { Button, Card, Input, Pagination, Row, Select, Spin, Table } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { PAGE_SIZE_OPTIONS } from '../WorkOrderTable/config/pagination/pagination';
import queryString from 'querystring';
import { GLOBAL_DEBOUNCE_TIME } from '../../utils/DataConstants';
import { debounce } from '../../utils/PerformanceUtils';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { parseURL } from '../../thunks/work_orders_thunks_wrapper';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import useMountEffect from '../../hooks/useMountEffect';
const TARGET_COLLECTION_NAME = 'allSupplierFacilitiesEver';
const ADVANCED_FILTERS_TARGET_COLLECTION_NAME = 'scorecardAdvancedFilters';

const defaultPagination = {
	current: 1,
	pageSize: 10,
};

const renderAverageDuration = (value) => {
	if (value === '-') return '-';
	const parsed = parseFloat(String(value));
	return isNaN(parsed) || parsed <= 0 ? '' : formatDurationShort(parsed);
};

const renderFirstTimeFixRate = (value) => {
	if (value === '-') return '-';
	const parsed = parseFloat(String(value));
	return isNaN(parsed) ? '' : `${roundToOneDecimalPlace(parsed)}%`;
};

const SLICE_DATE_FIELDS = [
	'startDate',
	'endDate',
	'workCreatedAtStartDate',
	'workCreatedAtEndDate',
];

const parseFilterDatesFromUrl = (filters) => {
	const next = { ...(filters || {}) };
	SLICE_DATE_FIELDS.forEach((field) => {
		const value = next[field];
		if (value && typeof value.format !== 'function') {
			const parsed = moment(String(value), 'YYYY-MM-DD');
			next[field] = parsed.isValid() ? parsed : undefined;
		}
	});
	return next;
};

const buildDefaultDateRange = () => ({
	startDate: moment().subtract(DEFAULT_COMPLETED_AT_SPAN_MONTHS, 'months').startOf('day'),
	endDate: moment().endOf('day'),
});

const SORT_KEY_BY_DATA_INDEX = {
	name: 'name',
	lifetimeTotalWos: 'lifetimeTotalWos',
	completedWos: 'completedWos',
	avgResponseTimeSeconds: 'responseTime',
	avgResolutionTimeSeconds: 'resolutionTime',
	firstTimeFixRate: 'firstTimeFix',
};

const buildScorecardApiParams = (sliceFilters) => {
	const params = supplierPerformanceFiltersToApiParams(parseFilterDatesFromUrl(sliceFilters));
	const searchText = sliceFilters && sliceFilters.name;
	if (searchText) {
		params.search = searchText;
	}
	return params;
};

const stringifyDateFields = (patch: Record<string, any>): Record<string, any> => {
	const converted = { ...patch };
	['startDate', 'endDate', 'workCreatedAtStartDate', 'workCreatedAtEndDate'].forEach((field) => {
		const value = converted[field];
		if (value && typeof value.format === 'function') {
			converted[field] = value.format('YYYY-MM-DD');
		}
	});
	return converted;
};

function VendorScorecardComp({
	data,
	error,
	history,
	location,
	supplierFacilities,
	handleQueryParams,
	targetCollectionName = TARGET_COLLECTION_NAME,
	fetchRecords,
	updatePagination,
	updateSorting,
	updateFilters,
	userType,
}) {
	const {
		fetching,
		filters: activeFilters,
		pagination,
		sorting: activeSorters,
	} = supplierFacilities || standardInitialStateGenerator([targetCollectionName]);
	const [vendorInfo, setVendorInfo] = useState(undefined);
	const activeFilterName = nullSafeGetOrElse('name', activeFilters, '');
	const [searchValue, setSearchValue] = useState(activeFilterName);
	useEffect(() => {
		setSearchValue(activeFilterName);
	}, [activeFilterName]);
	const applySearchRef = useRef<(searchText: string) => void>(() => {});
	applySearchRef.current = (searchText: string) => {
		const trimmed = searchText && searchText.trim();
		updateFilters(setVendorInfo)({ name: trimmed ? trimmed : undefined }, TARGET_COLLECTION_NAME);
	};
	const debouncedApplySearch = useMemo(
		() =>
			debounce((searchText: string) => applySearchRef.current(searchText), GLOBAL_DEBOUNCE_TIME),
		[]
	);
	const handleSearchChange = (event) => {
		setSearchValue(event.target.value);
		debouncedApplySearch(event.target.value);
	};

	const initialLoading = vendorInfo === undefined;
	const loading = fetching || initialLoading;

	useMountEffect(() => {
		const hasDateRange = activeFilters && activeFilters.startDate && activeFilters.endDate;
		const hasWoTypeFilter = activeFilters && activeFilters.isPM !== undefined;
		const initialFilters = stringifyDateFields({
			...(activeFilters || {}),
			...(hasDateRange ? {} : buildDefaultDateRange()),
			...(hasWoTypeFilter ? {} : { isPM: 'false' }),
		});
		fetchRecords(setVendorInfo)(location)(initialFilters);
	});

	const columns = useMemo(
		() => [
			{
				title: 'Suppliers',
				dataIndex: 'name',
				sorter: true,
				render: (text, record) => {
					const spendCategories = nullSafeGetOrElse('categoryNames', record, '').split(',');
					return (
						<div style={{ display: 'flex', alignItems: 'center' }} className="cursor-pointer">
							<div>
								<div
									style={{
										fontSize: 18,
										wordBreak: 'break-word',
									}}
								>
									{nullSafeGet('name', record)}
								</div>
								<div style={{ marginBottom: 0, fontSize: 14 }}>
									<div
										style={{ wordBreak: 'normal', whiteSpace: 'normal', color: 'rgba(0,0,0,0.45)' }}
									>
										{spendCategories
											.map((categoryName) => capitalizeEachWord(categoryName))
											.slice(0, 2)
											.join(', ')}
										{spendCategories.length > 3 ? ' and more' : null}
									</div>
								</div>
								<AddressDisplay
									hidePostcode={true}
									hideCountry={true}
									hideStreetAddress={true}
									address={record.primaryAddress}
									style={{
										color: 'rgba(0,0,0,0.45)',
									}}
								/>
							</div>
						</div>
					);
				},
			},
			{
				title: (
					<div>
						<div>Total Lifetime WOs</div>
						<div className="footnoteregular text-character-secondary">All time</div>
					</div>
				),
				dataIndex: 'lifetimeTotalWos',
				sorter: true,
				render: (text) => text,
			},
			{
				title: 'Completed WOs',
				dataIndex: 'completedWos',
				sorter: true,
				render: (value) => (value === null || value === undefined ? '--' : value),
			},
			{
				title: 'Response Time [Avg]',
				dataIndex: 'avgResponseTimeSeconds',
				sorter: true,
				render: renderAverageDuration,
			},
			{
				title: 'Resolution time [Avg]',
				dataIndex: 'avgResolutionTimeSeconds',
				sorter: true,
				render: renderAverageDuration,
			},
			{
				title: 'First time fix',
				dataIndex: 'firstTimeFixRate',
				sorter: true,
				render: renderFirstTimeFixRate,
			},
		],
		[vendorInfo]
	);

	const activeSortBy = nullSafeGet('sort_by', activeSorters);
	const activeSortOrder = nullSafeGetOrElse('order', activeSorters, null);
	const columnsWithSortOrder = columns.map((column) => ({
		...column,
		sortOrder: SORT_KEY_BY_DATA_INDEX[column.dataIndex] === activeSortBy ? activeSortOrder : null,
	}));

	const onRow = (record) => ({
		onClick: () => {
			const path = `/${userType}/analytics/kpi/vendors/detail/${record.id}`;
			const passthroughFilters = queryString.stringify(activeFilters);
			const link = `${getLinkWIthBackLinkParams(
				location,
				'Back to vendors',
				path
			)}&${passthroughFilters}`;
			history.push(link);
		},
	});

	const appliedFilters = useMemo(() => parseFilterDatesFromUrl(activeFilters), [activeFilters]);

	const updateAnalyticsFilters = useCallback(
		(patch) => updateFilters(setVendorInfo)(stringifyDateFields(patch), TARGET_COLLECTION_NAME),
		[updateFilters]
	);

	return (
		<>
			<AnalyticsControlBar
				userType={userType}
				updateFilters={updateAnalyticsFilters}
				startDate={appliedFilters.startDate}
				endDate={appliedFilters.endDate}
				dateRangeLabel="Completed Between"
				locationIds={appliedFilters.locationIds}
				supplierFacilityIds={appliedFilters.supplierFacilityIds}
				problemTypeIds={appliedFilters.problemTypeIds}
				woPriorityIds={appliedFilters.woPriorityIds}
				isPM={appliedFilters.isPM}
				workCreatedAtStartDate={appliedFilters.workCreatedAtStartDate}
				workCreatedAtEndDate={appliedFilters.workCreatedAtEndDate}
				filterConfig={{
					problemTypes: true,
					workOrderPriorities: true,
					isPM: true,
					workCreatedAt: true,
					isSupplierAnalytics: true,
				}}
				hideLocationsControl={false}
				hideSuppliersControl={false}
				showSearch={true}
				showDownload={false}
				showPrint={false}
				onSearch={handleSearchChange}
				search={searchValue}
				searchPlaceholder="Search"
			/>

			<div className={'mt-4'}>
				{vendorInfo === undefined || (vendorInfo && vendorInfo.length > 0) ? (
					<Card>
						<Table
							columns={columnsWithSortOrder}
							showHeader={true}
							rowKey={(el) => el['id']}
							dataSource={vendorInfo}
							loading={loading}
							onRow={onRowWithRangeSelection(onRow)}
							pagination={{
								current: nullSafeGetOrElse('current', pagination, 1),
								pageSize: nullSafeGetOrElse('pageSize', pagination, 10),
								total: nullSafeGetOrElse('total', pagination, 0),
								showSizeChanger: false,
							}}
							onChange={(tablePagination, tableFilters, tableSorter) => {
								const hasSort = !!(tableSorter && tableSorter.order);
								const sortedField = tableSorter && (tableSorter.columnKey || tableSorter.field);
								const nextSortBy = hasSort
									? SORT_KEY_BY_DATA_INDEX[sortedField] || 'lifetimeTotalWos'
									: 'lifetimeTotalWos';
								const nextOrder = hasSort ? tableSorter.order : 'descend';
								const currentSortBy = nullSafeGet('sort_by', activeSorters);
								const currentOrder = nullSafeGet('order', activeSorters);
								if (nextSortBy !== currentSortBy || nextOrder !== currentOrder) {
									updateSorting({ sort_by: nextSortBy, order: nextOrder }, setVendorInfo);
								} else {
									updatePagination(
										{ current: tablePagination.current, pageSize: tablePagination.pageSize },
										setVendorInfo
									);
								}
							}}
						/>
					</Card>
				) : !activeFilters ? (
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/sloth.svg"
							/>
						}
						headline={'My work here is done'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									You haven't added any suppliers to your private network yet. Once you've added
									them, we'll start keeping track of their performance here.
								</div>
								<Button
									type="primary"
									onClick={() => this.props.history.push(`/${userType}/dashboards/overview/all`)}
								>
									Find vendors
								</Button>
							</div>
						}
					/>
				) : (
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/sloth.svg"
							/>
						}
						headline={`Couldn't find any suppliers matching these filters`}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Change or clear your filters to see your suppliers here
								</div>
							</div>
						}
					/>
				)}
			</div>
		</>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	handleQueryParams: ownProps.handleQueryParams,
	currentUser: state.session.currentUser,
	spendCategories: state.spend_categories,
	regions: state.regions,
	areas: state.areas,
	fieldTechs: state.field_techs,
	supplierFacilities:
		state.supplier_facilities[ownProps.targetCollectionName || TARGET_COLLECTION_NAME],
	companyConfig: state.company_config.detail,
	targetCollectionName: ownProps.targetCollectionName,
	data: getEntitiesById(
		state.supplier_facilities[ownProps.targetCollectionName || TARGET_COLLECTION_NAME]
			? state.supplier_facilities[ownProps.targetCollectionName || TARGET_COLLECTION_NAME]
					.recordsIds || []
			: [],
		state.supplier_facilities.records
	),
});

const fetchSearchData =
	(userType) =>
	(
		handleQueryParams,
		history,
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
	(dispatch) => {
		if (handleQueryParams) {
			const queryParams = queryString.stringify({
				...{
					current: pagination.current || 10,
					pageSize: pagination.pageSize || 10,
				},
				...sorting,
				...filters,
				...params,
			});
			history.replace(`${window.location.pathname}?${queryParams}`);
		}
		return dispatch(
			supplierScorecardsRestCrudThunks(userType).read(
				{ ...(params || {}), ...buildScorecardApiParams(filters) },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	};

const fetchSupplierFacilities =
	(userType) =>
	(callback) =>
	(handleQueryParams, history, params, targetCollectionName, pagination, sorting, filters) =>
	(dispatch) =>
		dispatch(
			fetchSearchData(userType)(
				handleQueryParams,
				history,
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				false
			)
		).then((rows = []) =>
			callback(
				(rows || []).map((row) => ({
					...row,
					id: row.supplierFacilityId,
					name: row.displayName || row.name,
				}))
			)
		);

const fetchSupplierData =
	(userType) =>
	(callback, location) =>
	(
		handleQueryParams,
		history,
		targetCollectionName,
		preAppliedFilters,
		initialPagination,
		initialSorting,
		initialFilters
	) =>
	(dispatch) => {
		const { pagination, sorting, filters } = parseURL(
			handleQueryParams,
			history,
			location,
			{},
			initialPagination,
			initialSorting,
			initialFilters
		);

		return dispatch(
			fetchSupplierFacilities(userType)(callback)(
				false,
				history,
				{},
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		);
	};

const updatePaginationThunk =
	(userType) =>
	(callback) =>
	(handleQueryParams, history, targetCollectionName, pagination) =>
	(dispatch, getState) => {
		const { sorting, filters } = getState()['supplier_facilities'][targetCollectionName];
		dispatch(
			fetchSupplierFacilities(userType)(callback)(
				handleQueryParams,
				history,
				{},
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		);
	};
const updateSortingThunk =
	(userType) =>
	(callback) =>
	(handleQueryParams, history, targetCollectionName, sorting) =>
	(dispatch, getState) => {
		const { pagination, filters } = getState()['supplier_facilities'][targetCollectionName];
		dispatch(
			fetchSupplierFacilities(userType)(callback)(
				handleQueryParams,
				history,
				{},
				targetCollectionName,
				{ ...pagination, current: 1 },
				sorting,
				filters
			)
		);
	};
const updateFilterThunk =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, filters, ignoreRefreshCounts, callback) =>
	(dispatch, getState) => {
		const {
			sorting,
			pagination,
			filters: storeFilters,
		} = getState()['supplier_facilities'][targetCollectionName];
		const mergedFilters = { ...storeFilters, ...filters };
		dispatch(SUPPLIER_FACILITIES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName));
		return dispatch(
			fetchSupplierFacilities(userType)(callback)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				{ ...pagination, current: 1 },
				sorting,
				mergedFilters
			)
		);
	};

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchRecords: (callback) => (location) => (filters) =>
		dispatch(
			fetchSupplierData(ownProps.userType)(callback, location)(
				true,
				ownProps.history,
				TARGET_COLLECTION_NAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination || { current: 1, pageSize: 10 },
				ownProps.initialSorting || { sort_by: 'lifetimeTotalWos', order: 'descend' },
				filters
			)
		),

	fetchSearch: (params, targetCollectionName) =>
		dispatch(
			fetchSearchData(ownProps.userType)(
				false,
				ownProps.history,
				params,
				targetCollectionName,
				{ current: 1, pageSize: 1000 },
				ownProps.sorting,
				ownProps.filters,
				false
			)
		),

	fetchSupplierRecords: (callback) => (location) =>
		dispatch(
			fetchSupplierFacilities(ownProps.userType)(callback)(
				true,
				ownProps.history,
				TARGET_COLLECTION_NAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting,
				ownProps.initialFilters
			)
		),
	updatePagination: (pagination, callback) =>
		dispatch(
			updatePaginationThunk(ownProps.userType)(callback)(
				true,
				ownProps.history,
				ownProps.targetCollectionName || TARGET_COLLECTION_NAME,
				pagination
			)
		),
	updateSorting: (sorting, callback) =>
		dispatch(
			updateSortingThunk(ownProps.userType)(callback)(
				true,
				ownProps.history,
				ownProps.targetCollectionName || TARGET_COLLECTION_NAME,
				sorting
			)
		),
	updateFilters: (callback) => (filter, targetCollectionName) =>
		dispatch(
			updateFilterThunk(ownProps.userType)(
				true,
				ownProps.history,
				targetCollectionName || TARGET_COLLECTION_NAME,
				filter,
				ownProps.ignoreRefreshCounts,
				callback
			)
		),

	clearAndUpdateFilters: (callback) => () =>
		dispatch(
			fetchSupplierFacilities(ownProps.userType)(callback)(
				true,
				ownProps.history,
				{},
				TARGET_COLLECTION_NAME,
				{ current: 1, pageSize: 10 },
				{ sort_by: 'lifetimeTotalWos', order: 'descend' },
				{}
			)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(VendorScorecardComp)
);

export default connect(
	(state) => ({
		userType: state.session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
